import { useState, useEffect, useCallback, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  ClipboardList,
  Clock,
  CheckCircle,
  FileText,
  AlertCircle,
  Plus,
} from 'lucide-react'
import { evaluationsApi } from '../../lib/api/evaluations'
import LoadingSpinner from '../../components/ui/LoadingSpinner'
import EvaluationFormInline from './EvaluationFormInline'
import { formatDateInTz } from '../../lib/utils/timezone'
import { useInterviewerTimezone } from '../../hooks/useInterviewerTimezone'

// ── Types ───────────────────────────────────────────────────────────────────

interface CompletableSession {
  source: 'session' | 'booking'
  source_id: number
  client_id: number
  client_name: string | null
  title: string
  scheduled_at: string
  duration_minutes: number
  evaluation_id: number | null
  // The linked evaluation's status, straight from the server — null when
  // evaluation_id is null. This is the single source of truth for status;
  // never re-derive it from a separately-paginated evaluations list.
  evaluation_status: string | null
  // A no-show the CLIENT missed. Still evaluable — the interviewer was there —
  // but flagged so nobody writes it up as an interview that took place.
  is_no_show?: boolean
  no_show_party?: string | null
  // Client-side only, never sent by the server. Set when this row was pulled in
  // by an ?evalId= deep link for an evaluation whose session no longer meets the
  // completable rule. It is shown so the link works, but it is not "pending
  // work" — see the header badge.
  is_archived?: boolean
}

// ── Helpers ─────────────────────────────────────────────────────────────────

function initials(name: string | null) {
  return (name || 'C').charAt(0).toUpperCase()
}

const AVATAR_COLORS = [
  '#1e2432', '#4f46e5', '#0891b2', '#059669', '#7c3aed', '#d97706',
]
function avatarColor(name: string | null) {
  const n = (name || 'C').charCodeAt(0)
  return AVATAR_COLORS[n % AVATAR_COLORS.length]
}

function StatusPill({ status }: { status: string | null }) {
  if (!status)
    return (
      <span className="evp-pill evp-pill--needs">
        <AlertCircle size={10} /> Needs Evaluation
      </span>
    )
  switch (status) {
    case 'draft':
      return <span className="evp-pill evp-pill--draft"><Clock size={10} /> Draft</span>
    case 'interviewer_review':
      return <span className="evp-pill evp-pill--review"><FileText size={10} /> Under Review</span>
    case 'delivered':
      return <span className="evp-pill evp-pill--done"><CheckCircle size={10} /> Delivered</span>
    default:
      return <span className="evp-pill evp-pill--draft">{status}</span>
  }
}

// ── Component ────────────────────────────────────────────────────────────────

export default function InterviewerEvaluations() {
  // Same source the dashboard uses, so a session cannot read 9:30 AM there and
  // 7:00 PM here.
  const interviewerTz = useInterviewerTimezone()
  const [searchParams] = useSearchParams()
  const autoBookingId = searchParams.get('bookingId') ? Number(searchParams.get('bookingId')) : null
  const autoEvalId = searchParams.get('evalId') ? Number(searchParams.get('evalId')) : null

  const [completable, setCompletable] = useState<CompletableSession[]>([])
  const [loading, setLoading] = useState(true)
  const [startError, setStartError] = useState<string | null>(null)

  // Selected state
  const [selectedItem, setSelectedItem] = useState<CompletableSession | null>(null)
  const [activeEvalId, setActiveEvalId] = useState<number | null>(null)
  const [starting, setStarting] = useState<number | null>(null) // source_id being created

  const autoSelectedRef = useRef(false)

  const fetchAll = useCallback(async () => {
    setLoading(true)
    try {
      const sessions = await evaluationsApi.getCompletableSessions().catch(() => [])
      const list: CompletableSession[] = Array.isArray(sessions) ? sessions : []
      setCompletable(list)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetchAll() }, [fetchAll])

  // Auto-select a booking when navigating from the dashboard
  useEffect(() => {
    if (!autoBookingId || loading || autoSelectedRef.current || completable.length === 0) return
    const match = completable.find((s) => s.source === 'booking' && s.source_id === autoBookingId)
    if (match) {
      autoSelectedRef.current = true
      handleSelectItem(match)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoBookingId, loading, completable])

  // Auto-open a specific evaluation when navigating from Reports page via ?evalId=
  useEffect(() => {
    if (!autoEvalId || loading || autoSelectedRef.current) return

    const match = completable.find((s) => s.evaluation_id === autoEvalId)
    if (match) {
      autoSelectedRef.current = true
      setSelectedItem(match)
      setActiveEvalId(autoEvalId)
      return
    }

    // The evaluation exists but its session is not in the completable list —
    // typically a past booking still sitting at 'confirmed' with no attendance
    // evidence, which the completable rule excludes. Reports links straight
    // here, so fetch the evaluation on its own and open it rather than leaving
    // the interviewer on an empty page with no way back to their own draft.
    autoSelectedRef.current = true
    let cancelled = false
    ;(async () => {
      try {
        const ev = await evaluationsApi.getEvaluation(autoEvalId)
        if (cancelled || !ev?.id) return
        const item: CompletableSession = {
          source: ev.session_id != null ? 'session' : 'booking',
          source_id: ev.session_id ?? ev.booking_id ?? 0,
          client_id: ev.client_id,
          client_name: ev.client_name ?? null,
          title: ev.session_title || 'Mock Interview Session',
          scheduled_at: ev.scheduled_at ?? '',
          duration_minutes: ev.duration_minutes ?? 60,
          evaluation_id: ev.id,
          evaluation_status: ev.status ?? null,
          is_archived: true,
        }
        setCompletable((prev) =>
          prev.some((s) => s.evaluation_id === ev.id) ? prev : [item, ...prev]
        )
        setSelectedItem(item)
        setActiveEvalId(ev.id)
      } catch {
        if (!cancelled) {
          setStartError('This evaluation could not be opened. It may have been removed.')
        }
      }
    })()
    return () => { cancelled = true }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoEvalId, loading, completable])

  const getItemStatus = (item: CompletableSession): string | null => item.evaluation_status

  const handleSelectItem = async (item: CompletableSession) => {
    setStartError(null)
    setSelectedItem(item)
    if (item.evaluation_id !== null) {
      setActiveEvalId(item.evaluation_id)
      return
    }
    // No evaluation yet — create one (server is idempotent: returns existing if one already exists)
    setStarting(item.source_id)
    setActiveEvalId(null)
    try {
      const payload =
        item.source === 'session'
          ? { session_id: item.source_id }
          : { booking_id: item.source_id }
      const result = await evaluationsApi.createEvaluation(payload)
      const evalId = result?.id ?? result?.data?.id
      if (evalId) {
        // Update the item in local state so it shows the new evalId. A freshly
        // created evaluation is always 'draft' server-side (EvaluationStatus.DRAFT).
        setCompletable((prev) =>
          prev.map((s) =>
            s.source === item.source && s.source_id === item.source_id
              ? { ...s, evaluation_id: evalId, evaluation_status: 'draft' }
              : s
          )
        )
        setSelectedItem((prev) =>
          prev && prev.source === item.source && prev.source_id === item.source_id
            ? { ...prev, evaluation_id: evalId, evaluation_status: 'draft' }
            : prev
        )
        setActiveEvalId(evalId)
      }
    } catch (err) {
      // Re-fetch completable sessions — the evaluation may now exist (e.g. race condition or stale data)
      try {
        const sessions = await evaluationsApi.getCompletableSessions().catch(() => [])
        const list: CompletableSession[] = Array.isArray(sessions) ? sessions : []
        setCompletable(list)
        const updated = list.find((s) => s.source === item.source && s.source_id === item.source_id)
        if (updated?.evaluation_id) {
          setSelectedItem(updated)
          setActiveEvalId(updated.evaluation_id)
          return
        }
      } catch {
        // ignore secondary fetch error
      }
      // If recovery failed, show an inline error (never use browser alert)
      const msg = err instanceof Error ? err.message : 'Failed to start evaluation'
      setStartError(msg)
      setSelectedItem(null)
    } finally {
      setStarting(null)
    }
  }

  const handleStatusChange = (newStatus: string) => {
    if (activeEvalId === null) return
    setCompletable((prev) =>
      prev.map((s) =>
        s.evaluation_id === activeEvalId ? { ...s, evaluation_status: newStatus } : s
      )
    )
  }

  if (loading) return <LoadingSpinner message="Loading evaluations..." />

  const isSelected = (item: CompletableSession) =>
    selectedItem?.source === item.source && selectedItem?.source_id === item.source_id

  return (
    <div className="evp-root">

      {/* ── Page header ──────────────────────────────────────────────────── */}
      <div className="evp-header">
        <div>
          <h1 className="page-title">Evaluations</h1>
          <p className="page-subtitle">
            Select a completed interview to write or review an evaluation
          </p>
        </div>
        <div className="evp-header-meta">
          <span className="evp-count-badge">
            <AlertCircle size={13} />
            {completable.filter((s) => {
                if (s.is_archived) return false
                const status = s.evaluation_status
                return !status || status === 'draft' || status === 'interviewer_review'
              }).length} pending
          </span>
        </div>
      </div>

      {/* ── Main split layout ─────────────────────────────────────────────── */}
      <div className="evp-split">

        {/* ── Left panel: Candidate list ─────────────────────────────────── */}
        <div className="evp-left">
          <div className="evp-left-head">
            <ClipboardList size={15} />
            <span>Interviews ({completable.length})</span>
          </div>

          {completable.length === 0 ? (
            <div className="evp-empty-list">
              <ClipboardList size={36} style={{ color: 'var(--text-muted)', marginBottom: 8 }} />
              <p>No completed interviews yet.</p>
            </div>
          ) : (
            <div className="evp-candidate-list">
              {completable.map((item) => {
                const status = getItemStatus(item)
                const selected = isSelected(item)
                const isCreating = starting === item.source_id

                return (
                  <button
                    key={`${item.source}-${item.source_id}`}
                    type="button"
                    className={`evp-candidate-item ${selected ? 'evp-candidate-item--active' : ''}`}
                    onClick={() => !isCreating && handleSelectItem(item)}
                    disabled={isCreating}
                  >
                    {/* Avatar */}
                    <div
                      className="evp-avatar"
                      style={{ background: selected ? 'white' : avatarColor(item.client_name) }}
                    >
                      <span style={{ color: selected ? 'var(--primary-navy)' : 'white' }}>
                        {initials(item.client_name)}
                      </span>
                    </div>

                    {/* Info */}
                    <div className="evp-candidate-info">
                      <div className="evp-candidate-name">
                        {item.client_name || 'Client'}
                      </div>
                      <div className="evp-candidate-role">
                        {item.is_no_show ? 'Candidate did not join' : 'Interview candidate'}
                      </div>
                      {item.scheduled_at && (
                        <div className="evp-candidate-date">
                          <Clock size={11} />
                          {formatDateInTz(item.scheduled_at, interviewerTz)}
                        </div>
                      )}
                    </div>

                    {/* Status / action */}
                    <div className="evp-candidate-right">
                      {item.is_archived && (
                        <span
                          className="evp-pill"
                          style={{ background: '#f1f5f9', color: '#64748b' }}
                          title="Opened from Reports. This session is no longer in the active evaluation list because it has no recorded attendance."
                        >
                          Past
                        </span>
                      )}
                      {item.is_no_show && (
                        <span
                          className="evp-pill evp-pill--draft"
                          style={{ background: '#fef2f2', color: '#dc2626' }}
                          title="The candidate did not join. Record what happened and complete the evaluation as usual."
                        >
                          No-show
                        </span>
                      )}
                      {isCreating ? (
                        <span className="evp-pill evp-pill--loading">
                          <Plus size={10} /> Starting…
                        </span>
                      ) : (
                        <StatusPill status={status} />
                      )}
                    </div>
                  </button>
                )
              })}
            </div>
          )}
        </div>

        {/* ── Right panel: Form or placeholder ──────────────────────────── */}
        <div className="evp-right">
          {startError && (
            <div className="evp-start-error">
              <AlertCircle size={16} style={{ flexShrink: 0, marginTop: 1 }} />
              <div>
                <strong>Could not open evaluation.</strong> {startError}
              </div>
              <button
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', padding: 0, lineHeight: 1 }}
                onClick={() => setStartError(null)}
              >
                ×
              </button>
            </div>
          )}
          {activeEvalId !== null && selectedItem ? (
            <EvaluationFormInline
              key={activeEvalId}
              evalId={activeEvalId}
              durationMinutes={selectedItem.duration_minutes}
              onStatusChange={handleStatusChange}
              timezone={interviewerTz}
            />
          ) : starting !== null ? (
            <div className="evp-placeholder">
              <div className="spinner" style={{ margin: '0 auto 16px' }} />
              <p className="evp-placeholder-title">Setting up evaluation…</p>
            </div>
          ) : (
            <div className="evp-placeholder">
              <div className="evp-placeholder-icon">
                <FileText size={36} />
              </div>
              <h3 className="evp-placeholder-title">Select an evaluation</h3>
              <p className="evp-placeholder-sub">
                Choose a pending evaluation from the list to begin
              </p>
            </div>
          )}
        </div>
      </div>

      <style>{styles}</style>
    </div>
  )
}

const styles = `
  /* ── Root ─────────────────────────────────────────────────────────────── */
  .evp-root {
    display: flex;
    flex-direction: column;
    gap: 24px;
    height: 100%;
  }

  /* ── Header ───────────────────────────────────────────────────────────── */
  .evp-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    flex-wrap: wrap;
  }
  .evp-header-meta { display: flex; align-items: center; gap: 12px; padding-top: 6px; }
  .evp-count-badge {
    display: inline-flex; align-items: center; gap: 6px;
    background: rgba(217,119,6,0.1); color: #d97706;
    font-size: 13px; font-weight: 600;
    padding: 6px 14px; border-radius: 20px;
  }

  /* ── Split layout ─────────────────────────────────────────────────────── */
  .evp-split {
    display: grid;
    grid-template-columns: 1fr 2fr;
    gap: 20px;
    align-items: start;
    min-height: 600px;
  }

  /* ── Left panel ───────────────────────────────────────────────────────── */
  .evp-left {
    background: white;
    border: 1px solid var(--border);
    border-radius: 16px;
    box-shadow: var(--shadow);
    overflow: hidden;
    display: flex;
    flex-direction: column;
    max-height: calc(100vh - 220px);
  }
  .evp-left-head {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 14px 18px;
    border-bottom: 1px solid var(--border);
    font-size: 13px;
    font-weight: 600;
    color: var(--text-dark);
    background: #fafbfc;
    flex-shrink: 0;
  }
  .evp-candidate-list {
    overflow-y: auto;
    flex: 1;
  }
  .evp-empty-list {
    padding: 40px 20px;
    text-align: center;
    color: var(--text-muted);
    font-size: 13px;
  }

  /* ── Candidate item ───────────────────────────────────────────────────── */
  .evp-candidate-item {
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    padding: 14px 16px;
    border: none;
    background: none;
    cursor: pointer;
    text-align: left;
    border-bottom: 1px solid var(--border);
    transition: background 0.15s;
  }
  .evp-candidate-item:hover:not(:disabled) {
    background: #f5f7fa;
  }
  .evp-candidate-item--active {
    background: var(--primary-navy) !important;
  }
  .evp-candidate-item:disabled { opacity: 0.7; cursor: default; }

  .evp-avatar {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    font-weight: 700;
    flex-shrink: 0;
    border: 2px solid transparent;
  }
  .evp-candidate-item--active .evp-avatar {
    border-color: rgba(255,255,255,0.3);
  }

  .evp-candidate-info { flex: 1; min-width: 0; }
  .evp-candidate-name {
    font-size: 14px;
    font-weight: 600;
    color: var(--text-dark);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .evp-candidate-item--active .evp-candidate-name,
  .evp-candidate-item--active .evp-candidate-role,
  .evp-candidate-item--active .evp-candidate-date { color: white; }

  .evp-candidate-role { font-size: 11px; color: var(--text-muted); margin-top: 1px; }
  .evp-candidate-date {
    display: flex; align-items: center; gap: 4px;
    font-size: 11px; color: var(--text-muted); margin-top: 4px;
  }

  .evp-candidate-right { flex-shrink: 0; }

  /* ── Status pills ─────────────────────────────────────────────────────── */
  .evp-pill {
    display: inline-flex; align-items: center; gap: 4px;
    font-size: 10px; font-weight: 600;
    padding: 3px 8px; border-radius: 4px;
    white-space: nowrap;
  }
  .evp-pill--needs   { background: rgba(217,119,6,0.12); color: #d97706; }
  .evp-pill--draft   { background: rgba(217,119,6,0.12); color: #d97706; }
  .evp-pill--review  { background: rgba(124,58,237,0.10); color: #7c3aed; }
  .evp-pill--done    { background: rgba(22,163,74,0.10);  color: #16a34a; }
  .evp-pill--loading { background: rgba(99,102,241,0.10); color: #6366f1; }

  .evp-candidate-item--active .evp-pill {
    background: rgba(255,255,255,0.2);
    color: white;
  }

  /* ── Right panel ──────────────────────────────────────────────────────── */
  .evp-right {
    background: white;
    border: 1px solid var(--border);
    border-radius: 16px;
    box-shadow: var(--shadow);
    overflow-y: auto;
    max-height: calc(100vh - 220px);
    min-height: 600px;
    scrollbar-gutter: stable;
  }

  /* ── Placeholder ──────────────────────────────────────────────────────── */
  .evp-placeholder {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    min-height: 500px;
    text-align: center;
    padding: 40px;
  }
  .evp-placeholder-icon {
    width: 72px; height: 72px;
    border-radius: 20px;
    background: #f1f5f9;
    display: flex; align-items: center; justify-content: center;
    color: var(--text-muted);
    margin-bottom: 20px;
  }
  .evp-placeholder-title {
    font-size: 18px; font-weight: 700;
    color: var(--text-dark); margin-bottom: 8px;
  }
  .evp-placeholder-sub {
    font-size: 14px; color: var(--text-muted);
    max-width: 260px; line-height: 1.5;
  }

  /* ── Start error banner ───────────────────────────────────────────────── */
  .evp-start-error {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 12px 16px;
    margin: 16px 16px 0;
    background: rgba(220,38,38,0.06);
    border: 1px solid rgba(220,38,38,0.25);
    border-radius: 10px;
    font-size: 13px;
    color: #dc2626;
    line-height: 1.5;
  }
  .evp-start-error > div { flex: 1; }

  /* ── Inline Form styles ───────────────────────────────────────────────── */
  .efi-form {
    padding: 24px;
    display: flex;
    flex-direction: column;
    gap: 20px;
  }

  .efi-section {
    background: #fafbfc;
    border: 1px solid var(--border);
    border-radius: 12px;
    padding: 18px 20px;
  }
  .efi-session-card    { background: linear-gradient(135deg, #f0f4ff 0%, #fafbfc 100%); }
  .efi-redflag-card    { background: #fff9f9; border-color: #fecaca; }
  .efi-practice-card   { background: #f0fdff; border-color: #bae6fd; }
  .efi-obs-card        { background: #f0fdff; border-color: #a5f3fc; }
  .efi-transcript-card { background: #f5f3ff; border-color: #ddd6fe; }
  .efi-ai-card         { background: #faf5ff; border-color: #e9d5ff; }
  .efi-tracker-card    { background: white; }

  .efi-section-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 14px;
    gap: 12px;
    flex-wrap: wrap;
  }
  .efi-section-title {
    font-size: 14px;
    font-weight: 700;
    color: var(--text-dark);
    display: flex;
    align-items: center;
    gap: 6px;
    margin: 0;
  }
  .efi-section-title--strength { color: #16a34a; }
  .efi-section-title--improve  { color: #d97706; }

  .efi-status-pill {
    display: inline-flex; align-items: center; gap: 5px;
    font-size: 11px; font-weight: 600;
    padding: 4px 10px; border-radius: 6px;
    flex-shrink: 0;
  }

  /* Tag badges */
  .efi-tag {
    font-size: 10px; font-weight: 600;
    padding: 2px 8px; border-radius: 4px;
    white-space: nowrap;
  }
  .efi-tag--red    { background: #fef2f2; color: #dc2626; }
  .efi-tag--blue   { background: #e0f2fe; color: #0369a1; }
  .efi-tag--indigo { background: #eef2ff; color: #4338ca; }

  /* Session details grid */
  .efi-session-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
  }
  .efi-session-item {
    display: flex; align-items: flex-start; gap: 10px;
    background: white; border: 1px solid var(--border);
    border-radius: 8px; padding: 10px 12px;
  }
  .efi-session-icon { color: var(--primary-navy); margin-top: 2px; flex-shrink: 0; }
  .efi-session-label {
    font-size: 10px; color: var(--text-muted);
    font-weight: 500; text-transform: uppercase; letter-spacing: 0.5px;
  }
  .efi-session-value { font-size: 13px; font-weight: 600; color: var(--text-dark); margin-top: 2px; }

  /* AI button */
  .efi-ai-btn {
    display: inline-flex; align-items: center; gap: 6px;
    background: #f3f0ff; color: #7c3aed;
    border: 1px solid #e0d9ff;
    border-radius: 8px; padding: 6px 14px;
    font-size: 12px; font-weight: 600; cursor: pointer;
    transition: background 0.15s;
  }
  .efi-ai-btn:hover:not(:disabled) { background: #ede9fe; }
  .efi-ai-btn:disabled { opacity: 0.6; cursor: default; }

  .efi-textarea { resize: vertical; min-height: 80px; }

  /* Scores */
  .efi-overall { display: flex; flex-direction: column; align-items: flex-end; gap: 6px; }
  .efi-scores-list { display: flex; flex-direction: column; gap: 2px; }
  .efi-score-row {
    display: grid;
    grid-template-columns: 1fr auto 130px;
    align-items: center;
    gap: 14px;
    padding: 12px 0;
    border-bottom: 1px solid var(--border);
  }
  .efi-score-row:last-child { border-bottom: none; }
  .efi-score-label { display: flex; flex-direction: column; gap: 3px; }
  .efi-score-name   { font-size: 13px; font-weight: 600; }
  .efi-score-desc   { font-size: 11px; color: var(--text-muted); margin-top: 1px; }
  .efi-score-weight {
    font-size: 10px; font-weight: 600;
    color: var(--primary-navy);
    background: rgba(30,36,50,0.07);
    display: inline-block;
    padding: 2px 7px; border-radius: 4px;
    width: fit-content; margin-top: 3px;
  }
  .efi-stars { display: flex; gap: 2px; }
  .efi-star {
    background: none; border: none; cursor: pointer; padding: 2px; color: #cbd5e1;
    transition: color 0.1s; display: flex; align-items: center;
  }
  .efi-star--filled { color: #d4af37; }
  .efi-star:disabled { cursor: default; }

  /* List editor */
  .efi-list-row {
    display: flex; align-items: center; gap: 8px; margin-bottom: 6px;
  }
  .efi-list-row--quote { align-items: flex-start; }
  .efi-list-dot {
    width: 7px; height: 7px; border-radius: 50%; flex-shrink: 0;
  }
  /* Single-line compact input for lists */
  .efi-line-input {
    flex: 1;
    padding: 6px 10px !important;
    font-size: 13px !important;
    height: 34px !important;
  }
  /* Multi-line quote input for transcript */
  .efi-quote-input {
    flex: 1;
    background: white !important;
    border-left: 3px solid #818cf8 !important;
    border-radius: 0 6px 6px 0 !important;
    font-style: italic;
    font-size: 13px !important;
    line-height: 1.5;
    resize: none;
    padding: 6px 10px !important;
  }
  .efi-remove-btn {
    background: none; border: none; cursor: pointer;
    color: #94a3b8; padding: 4px; flex-shrink: 0;
    display: flex; align-items: center;
    transition: color 0.15s;
  }
  .efi-remove-btn:hover { color: #dc2626; }
  .efi-add-btn {
    background: none; border: 1px dashed var(--border);
    border-radius: 6px; padding: 5px 12px;
    font-size: 12px; font-weight: 500;
    color: var(--text-muted); cursor: pointer; width: 100%;
    margin-top: 2px;
    transition: border-color 0.15s, color 0.15s;
  }
  .efi-add-btn:hover { border-color: var(--primary-navy); color: var(--primary-navy); }

  /* ── Approval card ────────────────────────────────────────────────────── */
  .efi-approval-card {
    border: 1.5px solid #fbbf24;
    border-radius: 14px;
    overflow: hidden;
    background: white;
  }
  .efi-approval-header {
    display: flex; align-items: flex-start; gap: 12px;
    padding: 18px 20px 14px;
    border-bottom: 1px solid #fde68a;
    background: linear-gradient(135deg, rgba(251,191,36,0.08), rgba(251,191,36,0.03));
  }
  .efi-approval-title {
    font-size: 15px; font-weight: 700; color: #92400e; margin: 0 0 4px;
  }
  .efi-approval-desc {
    font-size: 13px; color: #78350f; margin: 0; line-height: 1.5;
  }
  .efi-approval-check-wrap {
    display: block;
    padding: 16px 20px;
    cursor: pointer;
    background: linear-gradient(135deg, rgba(251,191,36,0.10), rgba(251,191,36,0.05));
  }
  .efi-approval-check-inner {
    display: flex; align-items: flex-start; gap: 12px;
  }
  .efi-checkbox {
    width: 18px; height: 18px; margin-top: 1px;
    accent-color: #d97706; cursor: pointer; flex-shrink: 0;
  }
  .efi-checkbox:disabled {
    cursor: not-allowed; opacity: 0.35;
  }
  .efi-approval-check-text {
    font-size: 13px; font-weight: 500; color: #78350f; line-height: 1.5;
  }

  /* ── Tracker action buttons (below the tracker) ──────────────────────── */
  .efi-tracker-actions {
    display: flex; align-items: center;
    justify-content: flex-end; gap: 12px;
    margin-top: 24px;
    padding-top: 18px;
    border-top: 1px solid var(--border);
    flex-wrap: wrap;
  }
  .efi-save-msg { font-size: 13px; font-weight: 600; color: #16a34a; margin-right: auto; }
  .efi-approve-btn:disabled {
    opacity: 0.45 !important;
    cursor: not-allowed !important;
  }

  .efi-delivered-notice {
    display: flex; align-items: center; gap: 8px;
    background: #f0fdf4; border: 1px solid #bbf7d0;
    border-radius: 10px; padding: 14px 18px;
    font-size: 13px; font-weight: 500; color: #16a34a;
  }

  /* ── AI Summary section ──────────────────────────────────────────────── */
  .efi-ai-summary-box {
    display: flex; gap: 12px;
    background: white; border: 1px solid #e9d5ff;
    border-radius: 10px; padding: 14px 16px;
    margin-bottom: 12px;
    min-width: 0;
  }
  .efi-ai-summary-icon {
    width: 28px; height: 28px; border-radius: 8px;
    background: #f3f0ff; display: flex; align-items: center;
    justify-content: center; flex-shrink: 0; margin-top: 1px;
  }
  .efi-ai-summary-text {
    font-size: 13px; line-height: 1.7; color: var(--text-dark); margin: 0;
    flex: 1; min-width: 0; overflow-wrap: break-word; word-break: break-word;
    white-space: pre-wrap;
  }
  .efi-ai-summary-editable-wrap {
    margin-bottom: 12px;
  }
  .efi-ai-summary-badge {
    display: inline-flex; align-items: center; gap: 6px;
    font-size: 11px; font-weight: 600; color: #7c3aed;
    background: #f5f3ff; border: 1px solid #e9d5ff;
    border-radius: 6px; padding: 4px 10px; margin-bottom: 8px;
  }
  .efi-ai-summary-textarea {
    width: 100%; box-sizing: border-box;
    font-size: 13px; line-height: 1.7; color: var(--text-dark);
    border: 1.5px solid #e9d5ff; border-radius: 10px;
    padding: 14px 16px; resize: vertical; min-height: 160px;
    font-family: inherit; background: white;
    transition: border-color 0.15s, box-shadow 0.15s;
  }
  .efi-ai-summary-textarea:focus {
    outline: none; border-color: #a78bfa;
    box-shadow: 0 0 0 3px rgba(167,139,250,0.15);
  }
  .efi-ai-empty {
    display: flex; align-items: center; gap: 12px;
    background: white; border: 1px dashed #ddd6fe;
    border-radius: 10px; padding: 14px 16px; margin-bottom: 12px;
    font-size: 13px; color: var(--text-muted); line-height: 1.5;
  }
  .efi-ai-editor { margin-top: 4px; }
  .efi-ai-approve-row {
    display: flex; align-items: flex-start; gap: 10px;
    margin-top: 14px; padding: 12px 14px;
    background: white; border: 1px solid #e9d5ff;
    border-radius: 8px; cursor: pointer;
  }
  .efi-ai-approve-text {
    font-size: 12px; font-weight: 500; color: #6b21a8; line-height: 1.5; flex: 1;
  }
  .efi-ai-approved-badge {
    display: inline-flex; align-items: center; gap: 4px;
    background: #f0fdf4; color: #16a34a;
    font-size: 11px; font-weight: 700;
    padding: 3px 9px; border-radius: 5px; white-space: nowrap; flex-shrink: 0;
  }

  /* ── Voice / recording ───────────────────────────────────────────────── */
  .efi-voice-btn {
    display: inline-flex; align-items: center; gap: 6px;
    background: #f0fdff; color: #0891b2;
    border: 1px solid #a5f3fc; border-radius: 8px;
    padding: 6px 14px; font-size: 12px; font-weight: 600;
    cursor: pointer; transition: background 0.15s;
  }
  .efi-voice-btn:hover { background: #cffafe; }
  .efi-voice-btn--recording {
    background: #fef2f2; color: #dc2626;
    border-color: #fca5a5; animation: pulse-recording 1.5s ease-in-out infinite;
  }
  @keyframes pulse-recording {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.7; }
  }
  .efi-recording-indicator {
    display: flex; align-items: center; gap: 8px;
    background: #fef2f2; border: 1px solid #fca5a5;
    border-radius: 8px; padding: 8px 14px;
    font-size: 12px; font-weight: 600; color: #dc2626;
    margin-bottom: 10px;
  }
  .efi-recording-indicator--processing {
    background: #f5f3ff; border-color: #c4b5fd; color: #7c3aed;
  }
  .efi-rec-dot {
    width: 8px; height: 8px; border-radius: 50%; background: #dc2626;
    animation: blink 1s step-start infinite;
  }
  @keyframes blink { 50% { opacity: 0; } }
  .efi-obs-hint { font-size: 12px; color: var(--text-muted); margin-bottom: 10px; line-height: 1.5; }
  .efi-obs-empty { font-size: 12px; color: var(--text-muted); margin-top: 8px; font-style: italic; }

  /* ── AI Phrasing Suggestions ─────────────────────────────────────────── */
  .efi-phrasing-trigger-btn {
    display: inline-flex; align-items: center; gap: 6px;
    margin-top: 10px; padding: 6px 14px; font-size: 12px; font-weight: 600;
    border: 1px solid #c4b5fd; border-radius: 20px; background: #f5f3ff;
    color: #7c3aed; cursor: pointer; transition: background 0.2s;
  }
  .efi-phrasing-trigger-btn:hover { background: #ede9fe; }
  .efi-phrasing-trigger-btn:disabled { opacity: 0.6; cursor: not-allowed; }

  .efi-phrasing-panel {
    margin-top: 14px; background: #f8f7ff; border: 1px solid #ddd6fe;
    border-radius: 10px; padding: 16px; animation: fadeIn 0.2s ease;
  }
  @keyframes fadeIn { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: translateY(0); } }

  .efi-phrasing-header {
    display: flex; align-items: center; justify-content: space-between; margin-bottom: 4px;
  }
  .efi-phrasing-title { font-size: 14px; font-weight: 700; color: #5b21b6; }
  .efi-phrasing-close {
    background: none; border: none; cursor: pointer; font-size: 16px;
    color: var(--text-muted); padding: 0 4px; line-height: 1;
  }
  .efi-phrasing-close:hover { color: #dc2626; }
  .efi-phrasing-subtitle { font-size: 12px; color: var(--text-muted); margin-bottom: 12px; }

  .efi-phrasing-list { display: flex; flex-direction: column; gap: 10px; }
  .efi-phrasing-item {
    background: white; border: 1px solid #e9d5ff; border-radius: 8px;
    padding: 12px 14px; position: relative;
  }
  .efi-phrasing-label {
    font-size: 10px; font-weight: 700; letter-spacing: 0.6px;
    color: var(--text-muted); text-transform: uppercase; margin-right: 6px;
  }
  .efi-phrasing-original { margin-bottom: 4px; }
  .efi-phrasing-original-text {
    font-size: 13px; color: #dc2626; text-decoration: line-through;
  }
  .efi-phrasing-suggested { margin-bottom: 6px; }
  .efi-phrasing-suggested-text { font-size: 13px; font-weight: 600; color: #1e293b; }
  .efi-phrasing-reason {
    display: flex; align-items: center; gap: 4px;
    font-size: 11px; color: #7c3aed; font-style: italic; margin-bottom: 10px;
  }
  .efi-phrasing-apply {
    padding: 5px 14px !important; font-size: 12px !important;
    display: inline-flex; align-items: center; gap: 4px;
  }

  /* ── VTT Transcript list ─────────────────────────────────────────────── */
  .efi-vtt { margin-bottom: 18px; }
  .efi-vtt-head {
    display: flex; align-items: center; gap: 6px;
    font-size: 12px; font-weight: 600; color: #6366f1;
    margin-bottom: 8px;
  }
  .efi-vtt-count {
    margin-left: auto; font-size: 11px; font-weight: 500;
    color: var(--text-muted); background: #ede9fe;
    padding: 2px 8px; border-radius: 10px;
  }
  .efi-vtt-list {
    border: 1px solid #ddd6fe;
    border-radius: 10px;
    max-height: 340px;
    overflow-y: auto;
    background: white;
  }
  .efi-vtt-row {
    display: grid;
    grid-template-columns: 54px 1fr;
    gap: 12px;
    padding: 10px 14px;
    border-bottom: 1px solid #ede9fe;
    align-items: start;
  }
  .efi-vtt-row:last-child { border-bottom: none; }
  .efi-vtt-time {
    font-size: 11px; font-weight: 600; color: #6366f1;
    white-space: nowrap; padding-top: 3px;
    font-variant-numeric: tabular-nums;
  }
  .efi-vtt-body { display: flex; flex-direction: column; gap: 4px; min-width: 0; }
  .efi-vtt-speaker {
    font-size: 10px; font-weight: 700; text-transform: uppercase;
    letter-spacing: 0.5px; padding: 2px 7px; border-radius: 4px;
    width: fit-content;
  }
  .efi-vtt-text {
    font-size: 13px; color: var(--text-dark);
    margin: 0; line-height: 1.55;
    overflow-wrap: break-word; word-wrap: break-word;
  }
  @media (max-width: 480px) {
    .efi-vtt-row { grid-template-columns: 46px 1fr; gap: 8px; padding: 8px 10px; }
  }

  /* ── Transcript excerpts ─────────────────────────────────────────────── */
  .efi-list-row--quote { align-items: flex-start; }
  .efi-quote-input {
    background: white !important;
    border-left: 3px solid #818cf8 !important;
    border-radius: 0 6px 6px 0 !important;
    font-style: italic; font-size: 13px;
    line-height: 1.6; resize: vertical;
  }
  .efi-line-input { resize: none; height: 36px; overflow: hidden; }
  .efi-remove-btn {
    background: none; border: none; cursor: pointer;
    color: #94a3b8; padding: 4px; flex-shrink: 0;
    transition: color 0.15s;
  }
  .efi-remove-btn:hover { color: #dc2626; }

  /* ── Approval inline confirmed badge ─────────────────────────────────── */
  .efi-approved-inline {
    display: inline-flex; align-items: center; gap: 4px;
    background: #f0fdf4; color: #16a34a;
    font-size: 10px; font-weight: 700;
    padding: 2px 8px; border-radius: 4px; white-space: nowrap; flex-shrink: 0;
    margin-top: 2px;
  }

  /* ── Status Tracker ──────────────────────────────────────────────────── */
  .efi-tracker {
    display: flex;
    align-items: flex-start;
    position: relative;
    padding: 4px 0 8px;
  }
  .efi-tracker-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    position: relative;
  }
  .efi-tracker-line {
    position: absolute;
    top: 16px;
    right: 50%;
    width: 100%;
    height: 2px;
    background: #e2e8f0;
    z-index: 0;
    transform: translateX(-50%);
  }
  .efi-tracker-line--done   { background: #16a34a; }
  .efi-tracker-line--active { background: #3b82f6; }

  .efi-tracker-dot {
    width: 32px; height: 32px; border-radius: 50%;
    background: #f1f5f9; border: 2px solid #e2e8f0;
    display: flex; align-items: center; justify-content: center;
    font-size: 13px; font-weight: 700; color: #94a3b8;
    position: relative; z-index: 1;
    transition: all 0.3s;
  }
  .efi-tracker-dot--active {
    background: #dbeafe; border-color: #3b82f6; color: #1d4ed8;
    box-shadow: 0 0 0 4px rgba(59,130,246,0.15);
  }
  .efi-tracker-dot--done {
    background: #16a34a; border-color: #16a34a; color: white;
  }

  .efi-tracker-label {
    font-size: 12px; font-weight: 600; color: #94a3b8;
    margin-top: 8px;
  }
  .efi-tracker-label--active { color: #1d4ed8; }
  .efi-tracker-label--done   { color: #16a34a; }

  .efi-tracker-desc {
    font-size: 10px; color: var(--text-muted); margin-top: 2px;
  }

  /* ── Responsive ───────────────────────────────────────────────────────── */
  @media (max-width: 900px) {
    .evp-split { grid-template-columns: 1fr; }
    .evp-left  { max-height: 300px; }
    .evp-right { min-height: 400px; max-height: none; }
    .efi-two-col { grid-template-columns: 1fr; }
    .efi-score-row { grid-template-columns: 1fr; }
    .efi-session-grid { grid-template-columns: 1fr; }
  }

  @media (max-width: 480px) {
    .evp-left { max-height: 240px; }
    .evp-right { min-height: 300px; }
  }

  @media (max-width: 360px) {
    .evp-right { min-height: 260px; }
  }

  /* ── Spinner ──────────────────────────────────────────────────────────── */
  .spinner {
    width: 32px; height: 32px;
    border: 3px solid var(--border);
    border-top-color: var(--primary-navy);
    border-radius: 50%;
    animation: spin 0.7s linear infinite;
  }
  @keyframes spin { to { transform: rotate(360deg); } }
`
